import { uniqueId } from 'lodash/fp';
import { forwardRef, useMemo } from 'react';

import Checkbox, { CheckboxProps } from 'design-system/components/Checkbox';
import Input, { InputProps } from 'design-system/components/Input';
import RadioGroup, { RadioGroupProps } from 'design-system/components/RadioGroup';
import Select, { SelectProps } from 'design-system/components/Select';
import TextArea, { TextAreaProps } from 'design-system/components/TextArea';

import FormField, { FormFieldBaseProps } from './FormField';

export type ImplementsFormField = Omit<FormFieldBaseProps, 'children'>;

interface TextFieldProps extends ImplementsFormField, InputProps {}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, helperText, errorText, warningText, ...props },
  ref,
) {
  return (
    <FormField
      label={label}
      helperText={helperText}
      warningText={warningText}
      errorText={errorText}
    >
      <Input {...props} ref={ref} />
    </FormField>
  );
});

interface TextAreaFieldProps extends ImplementsFormField, TextAreaProps {}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  function TextAreaField({ label, helperText, errorText, warningText, ...props }, ref) {
    return (
      <FormField
        label={label}
        helperText={helperText}
        warningText={warningText}
        errorText={errorText}
      >
        <TextArea {...props} ref={ref} />
      </FormField>
    );
  },
);

interface SelectFieldProps extends ImplementsFormField, SelectProps {}

export const SelectField = ({
  id,
  label,
  helperText,
  errorText,
  warningText,
  children,
  ...props
}: SelectFieldProps) => {
  const linkedId = useMemo(() => id ?? uniqueId('pds-form-field-input-fields-select__'), [id]);

  return (
    <FormField
      label={label}
      labelProps={{ htmlFor: linkedId }}
      helperText={helperText}
      warningText={warningText}
      errorText={errorText}
    >
      <Select {...props} id={linkedId}>
        {children}
      </Select>
    </FormField>
  );
};

interface RadioGroupFieldProps extends ImplementsFormField, RadioGroupProps {}

export const RadioGroupField = ({
  label,
  helperText,
  errorText,
  warningText,
  children,
  ...props
}: RadioGroupFieldProps) => {
  return (
    <FormField
      label={label}
      helperText={helperText}
      warningText={warningText}
      errorText={errorText}
    >
      <RadioGroup {...props}>{children}</RadioGroup>
    </FormField>
  );
};

interface CheckboxFieldProps extends ImplementsFormField, CheckboxProps {
  checkboxLabel?: CheckboxProps['label'];
}

export const CheckboxField = ({
  label,
  helperText,
  errorText,
  warningText,
  checkboxLabel,
  ...props
}: CheckboxFieldProps) => {
  return (
    <FormField
      label={label}
      helperText={helperText}
      warningText={warningText}
      errorText={errorText}
    >
      <Checkbox label={checkboxLabel} {...props} />
    </FormField>
  );
};
